// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

import * as React from 'react';

import clsx from 'clsx';

import {
  AuxViewElement,
  FlowViewElement,
  ModuleViewElement,
  StockViewElement,
  ViewElement,
} from '@simlin/core/datamodel';
import { defined } from '@simlin/core/common';

import { displayName } from '../common';
import { cldLabelBox, wrapLabel, DEFAULT_LINE_HEIGHT } from '../../cld-label';

import styles from './CLDVariable.module.css';

/**
 * The element kinds that the CLD mode draws as nodes.  Cloud sources,
 * group containers, and aliases stay in the SFD-only set: a clean
 * causal-loop diagram doesn't surface infinite sources/sinks or
 * decorative grouping.
 */
export type CLDNodeElement = StockViewElement | FlowViewElement | AuxViewElement | ModuleViewElement;

export interface CLDVariableProps {
  element: CLDNodeElement;
  isSelected: boolean;
  isEditingName: boolean;
  isValidTarget?: boolean;
  hasWarning?: boolean;
  onSelection: (element: ViewElement, e: React.PointerEvent<SVGElement>, isText?: boolean) => void;
  onLabelDrag: (uid: number, e: React.PointerEvent<SVGElement>) => void;
}

// resvg-wasm >= 0.4 doesn't apply CSS class-based font properties to text, so
// the CLD label font is set inline (mirrors Label.tsx).  Inter/Arial matches
// the WS8a canvas notation and the 260803 loop-rendering reference.
const labelFontStyle: React.CSSProperties = {
  fontSize: '13px',
  fontFamily: "'Inter', 'Arial', 'Helvetica', sans-serif",
  fontWeight: 400,
  whiteSpace: 'pre',
};

/**
 * CO-5 text-only CLD variable node (supersedes the D2 criterion 15
 * shape-per-kind rendering).  In a causal-loop diagram the variable *is*
 * its name: there is no stock rectangle or aux circle.  We render the
 * display name centered on the node and back it with an **invisible box**
 * sized to the text -- the box is the hit target and the anchor links
 * dock against, so arrows meet the edge of the word rather than crossing
 * it (see the 260803 loop-rendering reference).  The box only becomes
 * visible on selection or as a link target.
 *
 * Reuses the same `onSelection` and `onLabelDrag` handlers that Canvas
 * already wires through to the SFD components, so click-select and
 * label-drag behave identically in both modes (D2 criterion 24: shared
 * infrastructure is reused, not duplicated).  Positions come straight
 * from `element.x` / `element.y` -- the same fields the SFD path consumes
 * (criterion 18); zoom/pan comes from the parent Canvas SVG transform.
 *
 * Deliberately omitted vs the SFD versions: sparklines, arrayed stacked
 * shadows, flow-pipe geometry, and the stock/aux glyphs themselves -- a
 * CLD surfaces causal structure, not stock-and-flow mechanics.
 */
export class CLDVariable extends React.PureComponent<CLDVariableProps> {
  handlePointerDown = (e: React.PointerEvent<SVGElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    this.props.onSelection(this.props.element, e);
  };

  handleLabelDoubleClick = (e: React.MouseEvent<SVGElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    // Double-click on the name enters rename, matching the SFD label:
    // forward as a text selection so Canvas opens the name editor.
    this.props.onSelection(this.props.element, e as unknown as React.PointerEvent<SVGElement>, true);
  };

  render(): React.ReactNode {
    const { element, isEditingName, isSelected, isValidTarget } = this.props;
    const cx = element.x;
    const cy = element.y;

    const lines = wrapLabel(displayName(defined(element.name)));
    const box = cldLabelBox(lines);
    const boxX = cx - box.width / 2;
    const boxY = cy - box.height / 2;
    // Baseline of the first line, so the whole block is vertically centered
    // on (cx, cy).  Each subsequent line steps down by one line height.
    const firstLineY = cy - ((lines.length - 1) * DEFAULT_LINE_HEIGHT) / 2;

    const className = clsx(styles.cldVariable, 'simlin-cld-variable', {
      [styles.selected]: isSelected && isValidTarget === undefined,
      'simlin-selected': isSelected && isValidTarget === undefined,
      [styles.targetGood]: isValidTarget === true,
      [styles.targetBad]: isValidTarget === false,
    });

    const label = isEditingName ? undefined : (
      <text
        className={styles.cldLabel}
        x={cx}
        y={firstLineY}
        textAnchor="middle"
        dominantBaseline="central"
        style={labelFontStyle}
        textRendering="optimizeLegibility"
        onDoubleClick={this.handleLabelDoubleClick}
      >
        {lines.map((line, i) => (
          <tspan key={`${i}:${line}`} x={cx} y={firstLineY + i * DEFAULT_LINE_HEIGHT}>
            {line}
          </tspan>
        ))}
      </text>
    );

    // Warning dot at the box's upper-right corner (SFD convention).
    const indicator = this.props.hasWarning ? (
      <circle className={styles.errorIndicator} cx={boxX + box.width} cy={boxY} r={3} />
    ) : undefined;

    return (
      <g className={className} onPointerDown={this.handlePointerDown}>
        {/* Invisible box: hit target + link/port anchor.  Transparent fill
            still receives pointer events; it only paints on select/target. */}
        <rect
          className={styles.hitBox}
          x={boxX}
          y={boxY}
          width={box.width}
          height={box.height}
          rx={6}
          ry={6}
        />
        {label}
        {indicator}
      </g>
    );
  }
}
